import React, { useState, useMemo } from 'react';
import { ArrowLeft, Users, Plus, X, Search, Shield, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUserAccess } from '../../../context/UserAccessContext';
import { useGuru } from '../../../context/GuruContext';
import { PageTransition } from '../../transitions/PageTransition';

export const PortalGuruPengaturanRole: React.FC = () => {
    const navigate = useNavigate();
    const { userPermissions, updateUserPermission, addUserPermission } = useUserAccess();
    const { guruRecords } = useGuru();

    const ROLES = [
        'depdik', 'sekretariat', 'mudir', 'yayasan', 'ketua divisi',
        'guru', 'staf', 'staf keuangan', 'staf tu', 'super_admin',
        'guru tahfidz', 'waka kurikulum', 'waka kesiswaan', 'wali kelas', 'murobbi'
    ];

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedGuruIds, setSelectedGuruIds] = useState<string[]>([]);
    const [selectedRole, setSelectedRole] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    // Pagination and List Search State
    const [listSearchQuery, setListSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const ITEMS_PER_PAGE = 20;

    // List of Guru that match search query and are NOT already selected in the modal
    const searchResults = useMemo(() => {
        if (!searchQuery.trim()) return [];
        return guruRecords.filter(g =>
            g.nama.toLowerCase().includes(searchQuery.toLowerCase()) &&
            !selectedGuruIds.includes(g.id)
        ).slice(0, 5); // Limit autocomplete results
    }, [guruRecords, searchQuery, selectedGuruIds]);

    const handleSelectGuru = (id: string) => {
        setSelectedGuruIds(prev => [...prev, id]);
        setSearchQuery('');
    };

    const handleRemoveSelectedGuru = (id: string) => {
        setSelectedGuruIds(prev => prev.filter(gId => gId !== id));
    };

    const handleSaveRole = async () => {
        if (selectedGuruIds.length === 0 || !selectedRole) return;
        setIsLoading(true);

        try {
            for (const guruId of selectedGuruIds) {
                const guru = guruRecords.find(g => g.id === guruId);
                if (!guru || !guru.email) continue;

                const existingPerm = userPermissions.find(p => p.email === guru.email);

                if (existingPerm) {
                    await updateUserPermission(existingPerm.id, { role: selectedRole });
                } else {
                    await addUserPermission({
                        email: guru.email,
                        role: selectedRole,
                        allowedMenus: []
                    });
                }
            }
            // Reset and close
            setSelectedGuruIds([]);
            setSelectedRole('');
            setIsModalOpen(false);
        } catch (error) {
            console.error("Error updating roles:", error);
            alert("Terjadi kesalahan saat menyimpan role.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteRole = async (permId: string) => {
        if (window.confirm('Hapus role pengguna ini?')) {
            await updateUserPermission(permId, { role: '' });
        }
    };

    // Filtering and Pagination Logic
    const filteredUsers = useMemo(() => {
        const usersWithRoles = userPermissions.filter(p => p.role && p.role.trim() !== '');
        if (!listSearchQuery.trim()) return usersWithRoles;

        return usersWithRoles.filter(perm => {
            const matchedGuru = guruRecords.find(g => g.email === perm.email);
            const nameMatch = matchedGuru && matchedGuru.nama.toLowerCase().includes(listSearchQuery.toLowerCase());
            const emailMatch = perm.email.toLowerCase().includes(listSearchQuery.toLowerCase());
            const roleMatch = perm.role?.toLowerCase().includes(listSearchQuery.toLowerCase());

            return nameMatch || emailMatch || roleMatch;
        });
    }, [userPermissions, guruRecords, listSearchQuery]);

    const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
    const paginatedUsers = filteredUsers.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    return (
        <div className="min-h-screen bg-gray-50 pb-20 font-sans text-gray-900">
            {/* Header */}
            <div className="bg-white border-b sticky top-0 z-30 shadow-sm">
                <div className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <ArrowLeft className="w-5 h-5 text-gray-600" />
                        </button>
                        <div>
                            <h1 className="text-lg font-bold text-gray-900 leading-tight">Setting Role</h1>
                            <p className="text-xs text-gray-500 font-medium">Manajemen Peran Sistem</p>
                        </div>
                    </div>
                </div>
            </div>

            <PageTransition className="p-4 max-w-3xl mx-auto space-y-6 mt-4">

                {/* Action Bar */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                    <h2 className="font-bold text-gray-700">Daftar Akun Ber-Role</h2>
                    <div className="flex w-full sm:w-auto items-center gap-3">
                        {/* Search Input */}
                        <div className="relative w-full sm:w-64">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <Search size={16} className="text-gray-400" />
                            </div>
                            <input
                                type="text"
                                placeholder="Cari nama, email, atau role..."
                                className="w-full pl-9 pr-8 py-2 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all text-sm"
                                value={listSearchQuery}
                                onChange={(e) => {
                                    setListSearchQuery(e.target.value);
                                    setCurrentPage(1); // Reset to page 1 on search
                                }}
                            />
                            {listSearchQuery && (
                                <button
                                    onClick={() => {
                                        setListSearchQuery('');
                                        setCurrentPage(1);
                                    }}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>

                        <button
                            onClick={() => setIsModalOpen(true)}
                            className="bg-brand-primary hover:bg-brand-primary-dark text-white text-sm font-bold py-2 px-4 rounded-xl flex items-center gap-2 shadow-sm transition-colors shrink-0"
                        >
                            <Plus size={16} />
                            <span className="hidden sm:inline">Tambah Role</span>
                        </button>
                    </div>
                </div>

                {/* List */}
                <div className="space-y-3">
                    {paginatedUsers.map(perm => {
                        const matchedGuru = guruRecords.find(g => g.email === perm.email);
                        const displayName = matchedGuru ? matchedGuru.nama : perm.email;

                        return (
                            <div key={perm.id} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100 shrink-0">
                                        <Shield className="w-5 h-5 text-purple-600" />
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-900">{displayName}</h3>
                                        <div className="flex items-center gap-2 mt-0.5">
                                            <span className="text-xs text-gray-500">{perm.email}</span>
                                            <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-brand-primary/10 text-brand-primary uppercase">
                                                {perm.role}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleDeleteRole(perm.id)}
                                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                                    title="Hapus Role"
                                >
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        );
                    })}

                    {paginatedUsers.length === 0 && (
                        <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-200">
                            <Shield className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                            <h3 className="font-bold text-gray-700">
                                {listSearchQuery ? 'Pencarian tidak ditemukan' : 'Belum ada role khusus'}
                            </h3>
                            <p className="text-xs text-gray-500 mt-1">
                                {listSearchQuery ? 'Coba gunakan kata kunci lain.' : 'Silakan tambah role untuk mendelegasikan akses sistem.'}
                            </p>
                        </div>
                    )}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="flex justify-center items-center gap-4 mt-6">
                        <button
                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                            className="p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                        >
                            <span className="material-symbols-outlined text-sm">chevron_left</span>
                        </button>
                        <span className="text-sm font-medium text-gray-600">
                            Halaman <span className="font-bold text-gray-900">{currentPage}</span> dari {totalPages}
                        </span>
                        <button
                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                            className="p-2 rounded-full bg-white border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
                        >
                            <span className="material-symbols-outlined text-sm">chevron_right</span>
                        </button>
                    </div>
                )}

            </PageTransition>

            {/* Modal Tambah Role */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}></div>
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md relative flex flex-col max-h-[85vh] animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center p-5 border-b border-gray-100 shrink-0">
                            <h3 className="font-bold text-lg text-gray-900">Tambah Role Akun</h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 bg-gray-50 hover:bg-gray-100 rounded-full transition-colors">
                                <X size={20} />
                            </button>
                        </div>

                        <div className="p-5 overflow-y-auto overscroll-contain space-y-5">

                            {/* Autocomplete Search */}
                            <div>
                                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Cari Guru / Staf</label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                        <Search size={18} className="text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        placeholder="Ketik nama untuk mencari..."
                                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all text-sm"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />

                                    {/* Dropdown Results */}
                                    {searchResults.length > 0 && (
                                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-100 rounded-xl shadow-lg max-h-48 overflow-y-auto">
                                            {searchResults.map(guru => (
                                                <button
                                                    key={guru.id}
                                                    onClick={() => handleSelectGuru(guru.id)}
                                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 last:border-0 flex flex-col"
                                                >
                                                    <span className="font-bold text-sm text-gray-900">{guru.nama}</span>
                                                    <span className="text-xs text-gray-500">{guru.email || 'Tidak ada email'}</span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Selected Teachers */}
                            {selectedGuruIds.length > 0 && (
                                <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                    <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Akun Terpilih ({selectedGuruIds.length})</label>
                                    <div className="flex flex-wrap gap-2">
                                        {selectedGuruIds.map(id => {
                                            const g = guruRecords.find(guru => guru.id === id);
                                            return (
                                                <div key={id} className="flex items-center gap-1.5 bg-white border border-gray-200 pl-3 pr-1.5 py-1.5 rounded-lg shadow-sm text-sm font-medium">
                                                    <span>{g?.nama.split(' ')[0]}</span>
                                                    <button
                                                        onClick={() => handleRemoveSelectedGuru(id)}
                                                        className="text-gray-400 hover:text-red-500 p-0.5 rounded-md hover:bg-red-50 transition-colors"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Role Select */}
                            <div>
                                <label className="block text-xs font-bold text-gray-600 uppercase tracking-wider mb-2">Pilih Role</label>
                                <select
                                    className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-brand-primary/20 focus:border-brand-primary outline-none transition-all text-sm font-medium text-gray-900"
                                    value={selectedRole}
                                    onChange={(e) => setSelectedRole(e.target.value)}
                                >
                                    <option value="" disabled>-- Pilih Role --</option>
                                    {ROLES.map(role => (
                                        <option key={role} value={role}>{role.toUpperCase()}</option>
                                    ))}
                                </select>
                            </div>

                        </div>

                        <div className="p-5 border-t border-gray-100 bg-gray-50/50 shrink-0">
                            <button
                                onClick={handleSaveRole}
                                disabled={isLoading || selectedGuruIds.length === 0 || !selectedRole}
                                className="w-full bg-brand-primary hover:bg-brand-primary-dark disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-xl shadow-md transition-all active:scale-[0.98]"
                            >
                                {isLoading ? 'Menyimpan...' : 'Simpan Pengaturan Role'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
